import { gsap } from "./gsapSetup";

export function initWebBackground({
                                    selector = ".web-bg",
                                    color = "rgba(0,0,0,0.25)",   // web line color
                                    nodeColor = "rgba(0,0,0,0.35)",
                                    density = 0.00009,                 // points per px^2 (tweak per section size)
                                    maxDist = 140,                     // connection distance
                                    mouseRadius = 160,                 // cursor influence radius
                                    drift = 14,                        // how much points mutate
                                    speed = 0.9,                       // how fast they drift
                                    strength = 0.35,                   // cursor push/pull strength
                                  } = {}) {
  const canvas = document.querySelector(selector);
  if (!canvas) return;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  const ctx = canvas.getContext("2d");
  let w = 0, h = 0, dpr = 1;

  const mouse = { x: -9999, y: -9999, active: false };

  let points = []; // {x,y, bx,by, tx,ty, vx,vy}
  let rafTween;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    w = Math.max(1, rect.width);
    h = Math.max(1, rect.height);
    dpr = Math.min(2, window.devicePixelRatio || 1);

    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // rebuild points on resize
    const count = Math.max(18, Math.floor(w * h * density));
    points = new Array(count).fill(0).map(() => {
      const x = Math.random() * w;
      const y = Math.random() * h;
      return {
        x, y,
        bx: x, by: y,               // base position
        tx: x + (Math.random() - 0.5) * drift,
        ty: y + (Math.random() - 0.5) * drift,
        vx: 0, vy: 0
      };
    });
  }

  function pickNewTargets() {
    // every few seconds, shift targets so the web “mutates”
    points.forEach(p => {
      p.tx = p.bx + (Math.random() - 0.5) * drift * 2;
      p.ty = p.by + (Math.random() - 0.5) * drift * 2;
    });

    gsap.to(points, {
      duration: 3.2 / speed,
      ease: "sine.inOut",
      x: (i, p) => p.tx,
      y: (i, p) => p.ty,
      overwrite: true,
      onComplete: () => {
        // pull bases slightly toward current position so it keeps evolving
        points.forEach(p => { p.bx = p.x; p.by = p.y; });
        gsap.delayedCall(0.6 / speed, pickNewTargets);
      }
    });
  }

  function draw() {
    ctx.clearRect(0, 0, w, h);

    // lines
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;

    for (let i = 0; i < points.length; i++) {
      const a = points[i];

      // mouse influence (soft repel)
      if (mouse.active) {
        const dx = a.x - mouse.x;
        const dy = a.y - mouse.y;
        const dist = Math.hypot(dx, dy);

        if (dist < mouseRadius && dist > 0.0001) {
          const t = 1 - dist / mouseRadius; // 0..1
          const push = t * t * strength * 22;
          a.x += (dx / dist) * push;
          a.y += (dy / dist) * push;
        }
      }

      for (let j = i + 1; j < points.length; j++) {
        const b = points[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.hypot(dx, dy);

        if (dist < maxDist) {
          const alpha = 1 - dist / maxDist;
          ctx.globalAlpha = alpha * 0.7;

          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }

    // nodes
    ctx.fillStyle = nodeColor;
    ctx.globalAlpha = 1;
    for (const p of points) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function startRaf() {
    // GSAP ticker is nice because you already use GSAP everywhere
    rafTween = gsap.ticker.add(draw);
  }

  function stopRaf() {
    if (rafTween) gsap.ticker.remove(draw);
    rafTween = null;
  }

  // events
  function onMove(e) {
    const rect = canvas.getBoundingClientRect();
    mouse.x = e.clientX - rect.left;
    mouse.y = e.clientY - rect.top;
    mouse.active = true;
  }
  function onLeave() {
    mouse.active = false;
    mouse.x = -9999;
    mouse.y = -9999;
  }

  // init
  resize();
  startRaf();
  pickNewTargets();

  window.addEventListener("resize", resize, { passive: true });
  window.addEventListener("mousemove", onMove, { passive: true });
  window.addEventListener("mouseleave", onLeave, { passive: true });

  // cleanup helper (useful if you have page transitions)
  return () => {
    stopRaf();
    gsap.killTweensOf(points);
    gsap.globalTimeline.clear(); // remove delayedCall if you want aggressive cleanup (optional)
    window.removeEventListener("resize", resize);
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseleave", onLeave);
  };
}
